<script setup lang="ts">
// The request someone is waiting on, above the console (onboarding, 2026-10-26): shown to someone in no workspace whose
// request is open, with Withdraw. It asks the server again every 20 s while the tab is visible (backing off to two
// minutes) and at once when the tab comes back, so an approval shows within seconds, without signing out.
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { getWelcome, withdrawJoin, type JoinRequest } from "@/api/workspaces";
import { useApi } from "@/composables/useApi";
import { useWorkspaceStore } from "@/stores/workspace";

const { client } = useApi();
const workspace = useWorkspaceStore();
const pending = ref<JoinRequest[]>([]);
const admins = ref<Record<string, string[]>>({});
const approved = ref<{ workspace: string; by: string | null } | null>(null);
const hidden = ref(false);
const error = ref("");
let timer: ReturnType<typeof setTimeout> | undefined;
let wait = 20_000;

const names = computed(() => pending.value.map((r) => r.workspace_name).join(" and "));
const who = computed(() => pending.value.length === 1 ? (admins.value[pending.value[0].workspace] ?? []).join(" or ") || "its admins" : "their admins");
const expires = computed(() => pending.value.length === 1
  ? new Date(pending.value[0].expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

async function check(): Promise<void> {
  await workspace.load();
  if (workspace.workspaces.length || workspace.me?.role === "admin") return;
  try {
    const w = await getWelcome(client);
    const was = pending.value.map((r) => r.workspace);
    pending.value = w.requests.filter((r) => r.status === "pending");
    admins.value = Object.fromEntries(w.workspaces.map((x) => [x.key, x.admins.map((a) => a.name)]));
    const yes = w.requests.find((r) => r.status === "approved" && was.includes(r.workspace));
    if (yes || w.entry === "done") approved.value = { workspace: yes?.workspace_name ?? "your workspace", by: yes?.decided_by ?? null };
    wait = 20_000;
  } catch {
    wait = Math.min(wait * 2, 120_000);   // a failed check waits longer; the page still works
  }
  schedule();
}
function schedule(): void {
  clearTimeout(timer);
  if (approved.value || !pending.value.length) return;   // nothing to wait for
  timer = setTimeout(() => { if (document.visibilityState === "visible") void check(); else schedule(); }, wait);
}
function onVisible(): void { if (document.visibilityState === "visible" && !approved.value) void check(); }
async function withdraw(): Promise<void> {
  const r = pending.value[0];
  if (!r) return;
  error.value = "";
  try {
    await withdrawJoin(client, r.workspace);
    pending.value = pending.value.slice(1);
  } catch (e) { error.value = (e as { body?: { error?: string } }).body?.error ?? "It wasn't withdrawn: try again."; }
}
async function carryOn(): Promise<void> {
  await workspace.load(true);   // their workspace, and what they may do in it, from the server
  approved.value = null;
}
onMounted(() => { void check(); document.addEventListener("visibilitychange", onVisible); });
onBeforeUnmount(() => { clearTimeout(timer); document.removeEventListener("visibilitychange", onVisible); });
</script>

<template>
  <div v-if="approved" class="join-banner ok" role="status">
    <span class="tag">Approved</span>
    <p>{{ approved.by ? `${approved.by} approved your request.` : "Your request was approved." }} You're in
      <b>{{ approved.workspace }}</b> now; no need to sign out.</p>
    <button type="button" class="btn btn--primary btn--sm" @click="carryOn">Continue</button>
  </div>
  <div v-else-if="pending.length && !hidden" class="join-banner" role="status">
    <span class="tag">Waiting</span>
    <p>Your request to join <b>{{ names }}</b> is with {{ who }}. Slack will tell you when they decide.
      <span v-if="expires" class="faint">Expires {{ expires }}</span></p>
    <span v-if="error" class="bad" role="alert">{{ error }}</span>
    <button type="button" class="btn btn--sm" @click="withdraw">Withdraw</button>
    <button type="button" class="icon-x" aria-label="Hide this banner" @click="hidden = true">×</button>
  </div>
</template>

<style scoped>
.join-banner { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; min-height: 48px;
  padding: var(--space-3) var(--space-6); border-bottom: 1px solid color-mix(in srgb, var(--warn) 30%, transparent);
  background: color-mix(in srgb, var(--warn) 12%, var(--bg)); color: var(--ink); font-size: var(--text-sm);
  animation: drop var(--speed-med) var(--ease-out) both; }
.join-banner.ok { border-bottom-color: color-mix(in srgb, var(--ok) 30%, transparent); background: color-mix(in srgb, var(--ok) 12%, var(--bg)); }
.join-banner p { margin: 0; flex: 1; min-width: min(100%, 280px); }
.tag { padding: 1px var(--space-3); border-radius: 999px; font-size: var(--text-xs); font-weight: 600;
  border: 1px solid color-mix(in srgb, var(--warn) 45%, var(--line)); color: var(--warn); }
.ok .tag { border-color: color-mix(in srgb, var(--ok) 45%, var(--line)); color: var(--ok); }
.faint { color: var(--faint); margin-left: var(--space-2); }
.bad { color: var(--danger); font-size: var(--text-xs); }
.icon-x { border: 0; background: transparent; color: var(--muted); font-size: 18px; line-height: 1; padding: var(--space-2); cursor: pointer;
  min-width: 32px; min-height: 32px; border-radius: var(--radius-s); }
.icon-x:hover { background: var(--bg-hover); }
@keyframes drop { from { opacity: 0; transform: translateY(-8px); } }
@media (prefers-reduced-motion: reduce) { .join-banner { animation: none; } }
</style>
